import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import HomeAction from 'home/actions/HomeAction'
import PropTypes from 'prop-types'
import { veryLightBlue, whiteColor } from 'utils/constants/ColorTheme'
import { Typography, Card, Grid, Stack } from '@mui/material'
import useFetch from 'utils/customHook/useFetch'
import { IDLE, LOADING, REJECTED } from 'store/DataManagerConstants'
import useDebounce from 'utils/customHook/useDebounce'
import ProgressBar from 'components/progress/ProgressBar'
import ApplicationConf from 'conf/ApplicationConf'
import { filter, trim, map, reduce, concat, template, isString } from 'lodash'
import { enrichTree, flattenTree, buildBreadcrumb, isFolder } from './FtpUtils'
import { FtpBreadcrumbsToolbar } from './FtpToolbar'
import FtpGridView from './FtpGridView'
import FtpListView from './FtpListView'
import FtpSearchResults from './FtpSearchResults'
import { push } from '@lagunovsky/redux-react-router'
import FileAction from './FileAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import i18n from 'simple-react-i18n'
import { DriveFileMove, UploadFile, CloudUpload } from '@mui/icons-material'


const GRID = 'grid'
const LIST = 'list'
const BATCH_SIZE = 60

const EmptyState = () => (
    <Stack alignItems='center' py={8} >
        <DriveFileMove sx={{ fontSize: '4rem', mb: 2, color: 'divider' }} />
        <Typography variant='body1' sx={{ fontWeight: 500, color: 'text.primary' }}>
            {i18n.ftpEmptyFolder}
        </Typography>
        <Typography variant='body2' mt={0.5}>
            {i18n.ftpDropFilesOrSubfolder}
        </Typography>
    </Stack>
)

const findNodeByPath = (node, target) => {
    if (!node) return null
    if (node.path === target) return node
    for (const child of (node.children ?? [])) {
        const found = findNodeByPath(child, target)
        if (found) return found
    }
    return null
}

const FileExplorer = ({
    directory = '/',
    withFiles = true,
    createIfNotExists = false,
    fileLimit = 10000,
}) => {
    const { data, status = IDLE, reload } = useFetch(ApplicationConf.files.system.fileTree(), {
        method: 'POST',
        body: { rootDir: directory, withFiles, createIfNotExists, fileLimit },
    })

    const treeRoot = useMemo(() => {
        if (!data) return null
        return enrichTree(data, '', 0, null)
    }, [data])

    const location = useLocation()
    const pathFromUrl = location.pathname.replace(/^\/job\/ftp\/?/, '/')
    const queryPathRaw = new URLSearchParams(location.search).get('path')
    const queryPath = queryPathRaw || (pathFromUrl !== '/' ? pathFromUrl : null)

    const [currentPath, setCurrentPath] = useState(queryPath || directory || '/')
    const [viewMode, setViewMode] = useState(GRID)
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
    const scrollContainerRef = useRef(null)
    const dispatch = useDispatch()
    const globalResearch = useSelector(store => store.HomeReducer.globalResearch)
    const searchQuery = globalResearch || ''
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

    useDebounce(() => {
        setDebouncedSearchQuery(searchQuery)
    }, 250, [searchQuery])

    useEffect(() => {
        if (!searchQuery) {
            setDebouncedSearchQuery('')
        }
    }, [searchQuery])

    const isPendingSearch = searchQuery !== debouncedSearchQuery && searchQuery !== ''

    useEffect(() => {
        const newPath = queryPath || directory || '/'
        if (newPath !== currentPath) {
            setCurrentPath(newPath)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryPath, directory, data])

    useEffect(() => {
        setVisibleCount(BATCH_SIZE)
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0
        }
    }, [currentPath])

    useEffect(() => {
        dispatch(HomeAction.updateGlobalResearch(''))
        return () => {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
    }, [dispatch])

    const currentNode = useMemo(() => {
        if (!treeRoot) return null
        return findNodeByPath(treeRoot, currentPath) || treeRoot
    }, [treeRoot, currentPath])

    const folders = useMemo(() => {
        return filter(currentNode?.children ?? [], isFolder)
    }, [currentNode])

    const files = useMemo(() => {
        return filter(currentNode?.children ?? [], c => !isFolder(c))
    }, [currentNode])

    const handleLoadMore = () => {
        const total = viewMode === GRID ? files.length : (folders.length + files.length)
        setVisibleCount((prev) => {
            if (prev < total) {
                return Math.min(prev + BATCH_SIZE, total)
            }
            return prev
        })
    }

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        if (scrollHeight - scrollTop - clientHeight < 350) {
            handleLoadMore()
        }
    }

    const crumbs = useMemo(() => {
        return currentNode ? buildBreadcrumb(currentNode) : []
    }, [currentNode])

    const isLoading = (status === LOADING || status === IDLE) && !data
    const isError = status === REJECTED
    const isSearching = trim(debouncedSearchQuery).length > 0

    const flattenedTree = useMemo(() => {
        return treeRoot ? flattenTree(treeRoot) : []
    }, [treeRoot])

    const searchResults = useMemo(() => {
        if (flattenedTree.length === 0 || !isSearching) return []
        const debouncedSQ = debouncedSearchQuery.toLowerCase().trim()
        return filter(flattenedTree, n => n.label && n.label.toLowerCase().includes(debouncedSQ))
    }, [flattenedTree, debouncedSearchQuery, isSearching])

    const handleNavigate = (folder) => {
        const path = isString(folder) ? folder : folder.path
        if (typeof folder === 'object' && folder.fileType !== 'folder') return
        dispatch(push(`/job/ftp${path === '/' ? '' : path}`))
        dispatch(HomeAction.updateGlobalResearch(''))
    }

    const handleNavigateToResult = (item) => {
        const lastSlash = item.path.lastIndexOf('/')
        const targetPath = item.fileType === 'folder'
            ? item.path
            : (lastSlash > 0 ? item.path.substring(0, lastSlash) : '/')
        dispatch(push(`/job/ftp${targetPath === '/' ? '' : targetPath}`))
        dispatch(HomeAction.updateGlobalResearch(''))
    }

    const dragCounter = useRef(0)
    const [isDragOver, setIsDragOver] = useState(false)
    const [isUploading, setIsUploading] = useState(false)

    const onDragEnter = (e) => {
        e.preventDefault()
        dragCounter.current += 1
        if (dragCounter.current === 1) setIsDragOver(true)
    }

    const onDragLeave = (e) => {
        e.preventDefault()
        dragCounter.current -= 1
        if (dragCounter.current === 0) setIsDragOver(false)
    }

    const onDragOver = (e) => {
        e.preventDefault()
    }

    const getUniqueFilename = (name, usedNames) => {
        if (!usedNames.includes(name)) return name
        const dotIndex = name.lastIndexOf('.')
        const base = dotIndex > 0 ? name.slice(0, dotIndex) : name
        const ext = dotIndex > 0 ? name.slice(dotIndex) : ''
        const findNext = (counter) => {
            const candidate = `${base} (${counter})${ext}`
            return usedNames.includes(candidate) ? findNext(counter + 1) : candidate
        }
        return findNext(1)
    }

    const onDrop = (e) => {
        e.preventDefault()
        dragCounter.current = 0
        setIsDragOver(false)
        if (!currentNode) return

        const droppedFiles = Array.from(e.dataTransfer.files)
        if (!droppedFiles.length) return
        const existingNames = map(files, f => f.label)

        setIsUploading(true)
        const { renamedFiles } = reduce(
            droppedFiles,
            (acc, file) => {
                const uniqueName = getUniqueFilename(file.name, acc.usedNames)
                return {
                    renamedFiles: concat(acc.renamedFiles, { file, name: uniqueName }),
                    usedNames: concat(acc.usedNames, uniqueName),
                }
            },
            { renamedFiles: [], usedNames: existingNames }
        )

        const readAsDataURL = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(file)
        })

        reduce(
            renamedFiles,
            (chain, { file, name }) => chain.then(() =>
                readAsDataURL(file).then(content =>
                    dispatch(FileAction.uploadFile(currentNode.path, name, content, () => { }, true))
                )
            ),
            Promise.resolve()
        ).then(() => {
            setIsUploading(false)
            dispatch(ToastrAction.success(template(i18n.ftpImportSuccess)({ count: droppedFiles.length })))
            reload()
        }).catch(() => {
            setIsUploading(false)
            dispatch(ToastrAction.error(i18n.ftpImportError))
            reload()
        })
    }

    const showContent = !isLoading && !isError && data
    const hasItems = folders.length > 0 || files.length > 0

    return (
        <Stack spacing={1} sx={{ height: 'calc(100vh - 80px)' }}>
            <FtpBreadcrumbsToolbar
                currentNode={currentNode}
                crumbs={crumbs}
                onNavigate={handleNavigate}
                viewMode={viewMode}
                setViewMode={setViewMode}
                onReload={reload}
            />

            <Card
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Grid
                    ref={scrollContainerRef}
                    overflow='auto'
                    padding={3}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onScroll={handleScroll}
                >
                    {isDragOver && (
                        <Stack
                            alignItems='center' justifyContent='center' gap={1}
                            sx={{
                                position: 'absolute', inset: 0, zIndex: 10,
                                backgroundColor: `${veryLightBlue}80`,
                                border: '2px dashed',
                                borderColor: 'primary.main',
                                borderRadius: 2,
                                pointerEvents: 'none',
                            }}
                        >
                            <UploadFile sx={{ fontSize: '3.5rem', color: 'primary.main' }} />
                            <Typography variant='body1' sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {template(i18n.ftpDropToImport)({ target: currentNode?.label || '/' })}
                            </Typography>
                        </Stack>
                    )}
                    {isUploading && (
                        <Stack
                            alignItems='center' justifyContent='center' gap={1}
                            sx={{ position: 'absolute', inset: 0, zIndex: 10, backgroundColor: `${whiteColor}80` }}
                        >
                            <CloudUpload sx={{ fontSize: '2.5rem', color: 'primary.main' }} />
                            <Typography variant='body2' sx={{ color: 'primary.main', fontWeight: 500 }}>
                                {i18n.ftpImportInProgress}
                            </Typography>
                        </Stack>
                    )}
                    {(isPendingSearch || isLoading) && <ProgressBar indeterminate withMessage />}

                    {isError && (
                        <Stack alignItems='center' py={6}>
                            <Typography variant='body1' sx={{ color: 'error.main' }}>
                                {i18n.ftpLoadError}
                            </Typography>
                        </Stack>
                    )}

                    {showContent && isSearching && (
                        <FtpSearchResults
                            searchResults={searchResults}
                            searchQuery={debouncedSearchQuery}
                            onNavigate={handleNavigateToResult}
                            onReload={reload}
                        />
                    )}

                    {showContent && !isSearching && !hasItems && (
                        <EmptyState />
                    )}

                    {showContent && !isSearching && hasItems && viewMode === GRID && (
                        <FtpGridView
                            folders={folders}
                            files={files}
                            visibleCount={visibleCount}
                            onLoadMore={handleLoadMore}
                            containerRef={scrollContainerRef}
                            onNavigate={handleNavigate}
                            onReload={reload}
                        />
                    )}

                    {showContent && !isSearching && hasItems && viewMode === LIST && (
                        <FtpListView
                            folders={folders}
                            files={files}
                            visibleCount={visibleCount}
                            onLoadMore={handleLoadMore}
                            containerRef={scrollContainerRef}
                            onNavigate={handleNavigate}
                            onReload={reload}
                        />
                    )}
                </Grid>
            </Card>
        </Stack>
    )
}

FileExplorer.propTypes = {
    directory: PropTypes.string,
    withFiles: PropTypes.bool,
    createIfNotExists: PropTypes.bool,
    fileLimit: PropTypes.number,
}

export { FileExplorer }